/**
 * CommandPalette — ⌘K / Ctrl+K global search palette. Direct port of the
 * prototype's `.cmd-palette` block (cms-redesign-mockup.html ~line 2735 for
 * styles, ~line 10242 for behavior).
 *
 * Tiers from the prototype (kept identical here):
 *   1. Pages   — every nav-enabled route from RouteConfig, permission-filtered
 *   2. Records — cross-resource content matches from globalSearch.js, run on
 *                every query and grouped by type, with the current section
 *                floated to the top. Only fires when the query is non-empty.
 *   3. Actions — workspace-level actions (Sign out for now)
 *
 * Open/close:
 *   • ⌘K / Ctrl+K toggles open globally (works from any focused element).
 *   • Esc closes.
 *   • Any element can request open by dispatching the `cms:open-palette`
 *     CustomEvent — that's how the TopBar's search-bar click hands off here.
 *
 * Keyboard within the open palette:
 *   • ↑ / ↓   move highlight (clamped to the result list)
 *   • Enter   selects the highlighted row
 *   • Esc     closes
 *
 * Mouse:
 *   • Hovering a row sets the highlight.
 *   • Clicking a row selects it.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useStore, useDispatch } from 'react-redux';
import SessionHelper from '../Helper/SessionHelper';
import RouteConfig from '../../constants/RouteConfig';
import BrandIcon from './BrandIcon';
import NAV_ICON_BY_PATH from './navIconMap';
import { runGlobalSearch, ensureContentLoaded, sectionKeyForPath } from './globalSearch';
import { cn } from './cn';

/* ─── Result paging ───────────────────────────────────────────────────── */

/**
 * How many records each section shows before the user asks for more.
 *
 * globalSearch returns EVERY match; this is purely how many are on screen at
 * once. The distinction matters — the original bug was a hard cap of 6 applied
 * at search time, which meant five of eleven "binaural" sessions were discarded
 * with no way to ever see them. Here nothing is discarded: the rest are one
 * click (or one ↓ and Enter) away.
 */
const INITIAL_PER_GROUP = 25;
const LOAD_MORE_STEP = 25;

/* ─── Scoring (matches prototype `score()` at line 10328) ──────────────── */

function score(item, q) {
  if (!q) return 0;
  const hay = (item.label + ' ' + (item.meta || '')).toLowerCase();
  const idx = hay.indexOf(q);
  if (idx === -1) return -1;
  if (item.label.toLowerCase().startsWith(q)) return 100 - idx;
  return 50 - idx;
}

/* ─── Page catalog (Tier 1) ──────────────────────────────────────────── */

/**
 * Build the page entries from RouteConfig — same shape as the prototype's
 * `buildPages()` (line 10313). Filtered by permissions; one entry per route
 * with a label.
 */
function buildPages() {
  return RouteConfig
    .filter((r) => r.label && r.path && r.requiresAuth !== false)
    .filter((r) => SessionHelper.hasPermission(r.requiredPermissions))
    // Hide the /searches list page — search is now the palette itself.
    .filter((r) => r.path !== '/searches')
    .map((r) => ({
      kind: 'page',
      label: r.label,
      meta: r.group || 'Workspace',
      icon: NAV_ICON_BY_PATH[r.path] || 'right_arrow',
      path: r.path,
    }));
}

/* ─── Actions catalog (Tier 3) ───────────────────────────────────────── */

function buildActions() {
  return [
    {
      kind: 'action',
      label: 'Sign out',
      meta: 'Account',
      icon: 'back_arrow',
      path: '/logout',
    },
  ];
}

/* ─── Palette ────────────────────────────────────────────────────────── */

export default function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [highlightIdx, setHighlightIdx] = useState(0);
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const navigate = useNavigate();
  const store = useStore();
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  // Float the current section's results to the top when on a list page.
  const currentSectionKey = useMemo(() => sectionKeyForPath(pathname), [pathname]);
  // One global adapter — fans out to every content source and returns flat
  // records tagged with their group. Always async (the API sources need it).
  const adapter = useMemo(() => ({
    async: true,
    getRecords: (query, { signal } = {}) => runGlobalSearch(query, store.getState(), signal),
  }), [store]);

  /* Open/close handlers */
  const openPalette = useCallback(() => {
    setOpen(true);
    setQuery('');
    setHighlightIdx(0);
  }, []);
  const closePalette = useCallback(() => setOpen(false), []);

  /* Focus the input once the modal mounts. */
  useEffect(() => {
    if (open && inputRef.current) {
      const t = setTimeout(() => inputRef.current?.focus(), 30);
      return () => clearTimeout(t);
    }
  }, [open]);

  /* Warm any empty content slices when the palette opens, so the first
   * cross-resource search is complete even from a cold page (e.g. Dashboard). */
  useEffect(() => {
    if (open) ensureContentLoaded(store.getState(), dispatch);
  }, [open, store, dispatch]);

  /* Cross-resource records — populated by the debounced global-search effect
   * below. Holds the flat, group-tagged record list from runGlobalSearch. */
  const [asyncRecords, setAsyncRecords] = useState({ items: [], loading: false });

  /* How many records each section is currently showing, keyed by groupKey.
   * Absent = INITIAL_PER_GROUP. Reset whenever the query changes. */
  const [groupLimits, setGroupLimits] = useState({});

  /* Pages + actions — recomputed on every query change.
   * Pages/actions match the prototype's `buildPages` / `buildActions`
   * (line 10313+). Sync records are filtered inline because they don't
   * need a network round-trip. */
  const { actionItems, pageItems } = useMemo(() => {
    const norm = query.trim().toLowerCase();
    const pages = buildPages();
    const actions = buildActions();

    const scoredPages = pages
      .map((p) => ({ p, s: score(p, norm) }))
      .filter((x) => x.s >= 0)
      .sort((a, b) => b.s - a.s);

    const scoredActions = actions
      .map((p) => ({ p, s: score(p, norm) }))
      .filter((x) => x.s >= 0)
      .sort((a, b) => b.s - a.s);

    // Same caps as the prototype: more pages when a query narrows things.
    const pageItems = scoredPages.slice(0, norm ? 12 : 8).map((x) => x.p);
    const actionItems = scoredActions.slice(0, 8).map((x) => x.p);

    return { actionItems, pageItems };
  }, [query]);

  /* Async records effect — debounce keystrokes, fire one request at a
   * time, abort the previous one when a newer query arrives. */
  useEffect(() => {
    const norm = query.trim().toLowerCase();
    if (!adapter || !adapter.async || !norm) {
      setAsyncRecords({ items: [], loading: false });
      return undefined;
    }

    let cancelled = false;
    const controller = new AbortController();
    setAsyncRecords((prev) => ({ items: prev.items, loading: true }));

    const timer = setTimeout(async () => {
      try {
        const raw = await adapter.getRecords(norm, { signal: controller.signal });
        if (cancelled) return;
        const items = (raw || []).map((r) => ({
          kind: 'record',
          label: r.label,
          meta:  r.meta || '',
          icon:  r.icon || 'right_arrow',
          path:  r.path,
          group: r.group,
          groupKey: r.groupKey,
        }));
        setAsyncRecords({ items, loading: false });
      } catch (e) {
        if (cancelled) return;
        // Axios returns `CanceledError`; native fetch throws `AbortError`.
        // Both are expected when we cancel a stale request — swallow them.
        if (e?.name === 'AbortError' || e?.name === 'CanceledError') return;
        // eslint-disable-next-line no-console
        console.warn('[CommandPalette] async records adapter failed:', e);
        setAsyncRecords({ items: [], loading: false });
      }
    }, 200);

    return () => {
      cancelled = true;
      controller.abort();
      clearTimeout(timer);
    };
  }, [adapter, query]);

  /* Records from the global adapter (always async). */
  const recordItems = asyncRecords.items;
  const recordsLoading = !!asyncRecords.loading;

  /* Group the flat records by source, floating the current section first, and
   * record each group's offset into the flattened order so keyboard highlight
   * indices line up with what's rendered. */
  const { recordGroups, orderedRecordItems } = useMemo(() => {
    const map = new Map();
    for (const item of recordItems) {
      const key = item.groupKey || 'records';
      if (!map.has(key)) map.set(key, { key, label: item.group || 'Results', offset: 0, items: [] });
      map.get(key).items.push(item);
    }
    const groups = [...map.values()];
    if (currentSectionKey) {
      groups.sort((a, b) => (b.key === currentSectionKey) - (a.key === currentSectionKey));
    }
    const ordered = [];
    for (const g of groups) {
      const matched = g.items;
      const limit = groupLimits[g.key] ?? INITIAL_PER_GROUP;
      g.offset = ordered.length;
      g.items = matched.slice(0, limit);
      g.total = matched.length;
      const hidden = matched.length - g.items.length;
      ordered.push(...g.items);
      // The "Show more" control is a row like any other so ↑/↓/Enter reach it
      // and it can't become a mouse-only affordance.
      g.more = hidden > 0
        ? {
          kind: 'more',
          groupKey: g.key,
          label: `Show ${Math.min(hidden, LOAD_MORE_STEP)} more`,
          meta: `${g.items.length} of ${matched.length}`,
          icon: 'right_arrow',
        }
        : null;
      if (g.more) ordered.push(g.more);
    }
    return { recordGroups: groups, orderedRecordItems: ordered };
  }, [recordItems, currentSectionKey, groupLimits]);

  /* Flat result list for keyboard nav — records first (grouped, current
   * section on top), then actions, then pages. */
  const flatResults = useMemo(
    () => [...orderedRecordItems, ...actionItems, ...pageItems],
    [orderedRecordItems, actionItems, pageItems],
  );

  /* Reset highlight and per-section paging whenever the query changes. */
  useEffect(() => {
    setHighlightIdx(0);
    setGroupLimits({});
  }, [query]);

  /* Run a selection — expand a section, or navigate to the item's path. */
  const runSelection = useCallback(
    (item) => {
      if (!item) return;
      if (item.kind === 'more') {
        // Reveal the next page in place; the palette stays open and the
        // highlight stays put, which lands it on the first newly-shown row.
        setGroupLimits((prev) => ({
          ...prev,
          [item.groupKey]: (prev[item.groupKey] ?? INITIAL_PER_GROUP) + LOAD_MORE_STEP,
        }));
        return;
      }
      closePalette();
      if (item.path) navigate(item.path);
    },
    [navigate, closePalette],
  );

  /* Global keydown — ⌘K toggles open, arrows/enter/esc act inside. */
  useEffect(() => {
    function onKey(e) {
      const cmdK = (e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k';
      if (cmdK) {
        e.preventDefault();
        setOpen((o) => !o);
        return;
      }
      if (!open) return;
      if (e.key === 'Escape') {
        e.preventDefault();
        closePalette();
        return;
      }
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setHighlightIdx((i) => Math.min(flatResults.length - 1, i + 1));
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setHighlightIdx((i) => Math.max(0, i - 1));
        return;
      }
      if (e.key === 'Enter') {
        e.preventDefault();
        runSelection(flatResults[highlightIdx]);
      }
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, flatResults, highlightIdx, closePalette, runSelection]);

  /* Listen for the imperative open event (dispatched by TopBar search). */
  useEffect(() => {
    function onOpenEvent() { openPalette(); }
    window.addEventListener('cms:open-palette', onOpenEvent);
    return () => window.removeEventListener('cms:open-palette', onOpenEvent);
  }, [openPalette]);

  /* Keep highlighted row in view as the user navigates. */
  useEffect(() => {
    if (!open || !listRef.current) return;
    const active = listRef.current.querySelector('[data-highlighted="true"]');
    if (active) active.scrollIntoView({ block: 'nearest' });
  }, [highlightIdx, open]);

  if (!open) return null;

  /* Mac vs everyone else — same detection as prototype JS. */
  const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform);

  // Rendered inline (not via React portal) because Tailwind utilities are
  // scoped to `#root` via `important: '#root'` in tailwind.config.js. A
  // portal to document.body would render the palette OUTSIDE #root and
  // break every Tailwind class on it. Position-fixed already covers the
  // viewport regardless of DOM location.
  return (
    <div
      // `.cmd-palette-backdrop` (2735–2747): fixed, navy/40 + 2px blur,
      // z-300, grid start center, padding-top 12vh. Open transition opacity.
      onClick={(e) => {
        if (e.target === e.currentTarget) closePalette();
      }}
      style={{
        position: 'fixed',
        inset: 0,
        background: '#2C3E5066',
        backdropFilter: 'blur(2px)',
        WebkitBackdropFilter: 'blur(2px)',
        zIndex: 300,
        paddingTop: '12vh',
        display: 'grid',
        placeItems: 'start center',
        animation: 'fadeIn 160ms ease forwards',
      }}
      role="presentation"
    >
      <div
        // `.cmd-palette` (2748–2762): max-width 580, surface, sand border,
        // lg radius, big two-layer shadow, max-height 70vh, slide+scale in.
        role="dialog"
        aria-label="Command palette"
        style={{
          width: '100%',
          maxWidth: 580,
          margin: '0 16px',
          background: 'rgb(var(--color-surface))',
          border: '1px solid rgb(var(--color-sand))',
          borderRadius: 12,
          boxShadow: '0 24px 60px #2C3E5033, 0 4px 12px #2C3E501A',
          overflow: 'hidden',
          maxHeight: '70vh',
          display: 'flex',
          flexDirection: 'column',
          animation: 'cmdPaletteIn 180ms cubic-bezier(0.4, 0, 0.2, 1) forwards',
        }}
      >
        {/* `.cmd-search` (2764–2790): padding 14 18, gap 10, border-bottom sand */}
        <div
          className="flex items-center border-b border-sand shrink-0"
          style={{ gap: 10, padding: '14px 18px' }}
        >
          {/* `.cmd-search [data-i]` — 16×16 text-muted */}
          <BrandIcon name="search" size={16} className="text-text-muted shrink-0" />
          {/* `.cmd-search input` (2774–2779): 15px navy, no border, transparent,
              padding 2 0 */}
          <input
            ref={inputRef}
            type="text"
            autoComplete="off"
            spellCheck={false}
            placeholder="Search content, pages, actions…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="flex-1 min-w-0 bg-transparent border-0 outline-none font-sans text-navy placeholder:text-text-muted"
            style={{ fontSize: 15, padding: '2px 0' }}
          />
          {/* `.cmd-search .kbd` (2781–2790): JetBrains Mono 10.5/600,
              padding 3 6, cream bg, sand border, sm radius, text-light */}
          <span
            className="font-mono font-semibold bg-cream border border-sand rounded-sm text-text-light shrink-0"
            style={{ fontSize: 10.5, padding: '3px 6px' }}
          >
            ESC
          </span>
        </div>

        {/* `.cmd-results` (2791–2794): overflow-y auto, flex 1, padding 6 6 8 */}
        <div
          ref={listRef}
          className="flex-1"
          style={{ overflowY: 'auto', padding: '6px 6px 8px' }}
        >
          {flatResults.length === 0 && !recordsLoading ? (
            // `.cmd-empty` (2829–2832): padding 28 16, center, text-muted 13px
            <div
              className="text-center text-text-muted"
              style={{ padding: '28px 16px', fontSize: 13 }}
            >
              No matches for &ldquo;{query}&rdquo;
            </div>
          ) : (
            <>
              {/* Records first — cross-resource matches, one Section per
               *  content type (current section floated first). Renders a
               *  "Searching…" placeholder while the search is in-flight and
               *  there are no prior results to show. */}
              {orderedRecordItems.length === 0 && recordsLoading && (
                <Section label="Results">
                  <div
                    className="text-text-muted italic"
                    style={{ padding: '10px 12px', fontSize: 13 }}
                  >
                    Searching…
                  </div>
                </Section>
              )}
              {recordGroups.map((group) => (
                <Section key={group.key} label={group.label}>
                  {group.items.map((item, j) => {
                    const idx = group.offset + j;
                    return (
                      <Row
                        key={`record-${group.key}-${j}`}
                        item={item}
                        highlighted={highlightIdx === idx}
                        onHover={() => setHighlightIdx(idx)}
                        onClick={() => runSelection(item)}
                      />
                    );
                  })}
                  {group.more && (() => {
                    const idx = group.offset + group.items.length;
                    return (
                      <Row
                        key={`more-${group.key}`}
                        item={group.more}
                        highlighted={highlightIdx === idx}
                        onHover={() => setHighlightIdx(idx)}
                        onClick={() => runSelection(group.more)}
                      />
                    );
                  })()}
                </Section>
              ))}
              {actionItems.length > 0 && (
                <Section label="Actions">
                  {actionItems.map((item, i) => {
                    const idx = orderedRecordItems.length + i;
                    return (
                      <Row
                        key={`action-${i}`}
                        item={item}
                        highlighted={highlightIdx === idx}
                        onHover={() => setHighlightIdx(idx)}
                        onClick={() => runSelection(item)}
                      />
                    );
                  })}
                </Section>
              )}
              {pageItems.length > 0 && (
                <Section label="Pages">
                  {pageItems.map((item, i) => {
                    const idx = orderedRecordItems.length + actionItems.length + i;
                    return (
                      <Row
                        key={`page-${i}`}
                        item={item}
                        highlighted={highlightIdx === idx}
                        onHover={() => setHighlightIdx(idx)}
                        onClick={() => runSelection(item)}
                      />
                    );
                  })}
                </Section>
              )}
            </>
          )}
        </div>

        {/* `.cmd-foot` (2833–2850): padding 8 14, border-top sand, cream bg,
            11px text-light, gap 14 */}
        <div
          className="flex items-center shrink-0 border-t border-sand bg-cream text-text-light"
          style={{ gap: 14, padding: '8px 14px', fontSize: 11 }}
        >
          <Hint label="navigate">{isMac ? '↑↓' : '↑↓'}</Hint>
          <Hint label="select">↵</Hint>
          <Hint label="close">ESC</Hint>
        </div>
      </div>
    </div>
  );
}

/* ─── Subcomponents ──────────────────────────────────────────────────── */

/** `.cmd-section` (2795–2803). */
function Section({ label, children }) {
  return (
    <div style={{ padding: '6px 4px 2px' }}>
      <div
        className="font-condensed font-bold uppercase text-text-muted"
        style={{ fontSize: 10.5, letterSpacing: '0.1em', padding: '6px 10px 4px' }}
      >
        {label}
      </div>
      {children}
    </div>
  );
}

/** `.cmd-row` (2805–2828): grid 22 1fr auto, gap 12, padding 9 10, rounded-md,
 *  13.5px text. Highlight: teal-green-soft bg + teal-green-dark text. */
function Row({ item, highlighted, onHover, onClick }) {
  return (
    <div
      role="option"
      aria-selected={highlighted}
      data-highlighted={highlighted ? 'true' : 'false'}
      onMouseEnter={onHover}
      onClick={onClick}
      className={cn(
        'grid items-center cursor-pointer',
        highlighted
          ? 'bg-teal-green-soft text-teal-green-dark'
          : 'text-text',
      )}
      style={{
        gridTemplateColumns: '22px 1fr auto',
        gap: 12,
        padding: '9px 10px',
        borderRadius: 8,
        fontSize: 13.5,
        transition: 'background 100ms ease',
      }}
    >
      {/* `.cmd-row [data-i]` — 18×18 currentColor opacity 0.85 */}
      <span style={{ width: 18, height: 18, opacity: 0.85 }}>
        <BrandIcon name={item.icon || 'right_arrow'} size={18} />
      </span>
      {/* Label — font-weight 500 */}
      <span style={{ fontWeight: 500 }}>{item.label}</span>
      {/* Meta — 11px Asap Condensed uppercase letter-spacing 0.08em */}
      <span
        className={cn(
          'font-condensed uppercase',
          highlighted ? 'text-teal-green-dark' : 'text-text-muted',
        )}
        style={{
          fontSize: 11,
          letterSpacing: '0.08em',
          opacity: highlighted ? 0.85 : 1,
        }}
      >
        {item.meta || ''}
      </span>
    </div>
  );
}

/** Footer hint label + kbd chip. Matches `.cmd-foot .hint` + `.cmd-foot .hint .kbd`. */
function Hint({ children, label }) {
  return (
    <span className="inline-flex items-center" style={{ gap: 5 }}>
      <span
        className="font-mono font-semibold bg-surface border border-sand rounded-sm text-text"
        style={{ fontSize: 10, padding: '2px 5px' }}
      >
        {children}
      </span>
      {label}
    </span>
  );
}
